import flow from 'lodash/flow'
import orderBy from 'lodash/orderBy'
import merge from 'lodash/merge'
import assign from 'lodash/assign'
import debounce from 'lodash/debounce'
import omit from 'lodash/omit'
import getDistanceBetween from '@utils/get-distance-between'

function createInitialState() {
  const sorts = [
    {
      label: 'name ▲',
      field: 'name',
      direction: 'asc',
      isFieldDefault: true,
    },
    {
      label: 'name ▼',
      field: 'name',
      direction: 'desc',
    },
    {
      label: 'distance ▲',
      field: 'distance',
      direction: 'asc',
      isFieldDefault: true,
      value: null,
      resultTag: 'miles away',
    },
    {
      label: 'distance ▼',
      field: 'distance',
      direction: 'desc',
      value: null,
      resultTag: 'miles away',
    },
  ]

  return {
    filters: {
      eligibility: {
        hasMi: null,
        hasIdd: null,
        gender: null,
        licensed: 'licensed',
      },
      query: '',
      onlyOpenings: true,
      genderOfCurrentResidents: null,
      hasPrivateBedrooms: false,
      wheelchairSupportRequired: false,
      behavioralIssues: false,
    },
    sorts,
    selectedSort: sorts[0],
    justUpdated: false,
  }
}

export const state = createInitialState()

export const getters = {
  results(state, getters, rootState) {
    const { filters } = state
    const normalizedQuery = filters.query.toLowerCase().trim()

    return flow(
      (properties) =>
        properties.filter(
          (property) =>
            (!normalizedQuery ||
              property.officialName.toLowerCase().includes(normalizedQuery) ||
              property.nickname.toLowerCase().includes(normalizedQuery) ||
              property.address.street.toLowerCase().includes(normalizedQuery) ||
              property.address.city.toLowerCase().includes(normalizedQuery) ||
              property.address.state.toLowerCase().includes(normalizedQuery) ||
              // property.address.zip.toLowerCase().includes(normalizedQuery) ||
              property.directServiceProviders.some((dsp) =>
                dsp.officialName.toLowerCase().includes(normalizedQuery)
              )) &&
            (!filters.onlyOpenings || property.openings.length) &&
            (filters.eligibility.licensed === 'licensed' ||
              property.licensed === 'No') &&
            (filters.eligibility.licensed === 'unlicensed' ||
              property.licensed === 'Yes') &&
            (!filters.eligibility.hasMi || property.miLicensed !== 'No') &&
            (!filters.eligibility.hasIdd || property.iddLicensed !== 'No') &&
            // (!filters.eligibility.hasMi || property.miexp !== 'No') &&
            // (!filters.eligibility.hasIdd || property.iddexp !== 'No') &&
            (!filters.eligibility.gender ||
              (property.openings.length
                ? property.openings.some(
                    (opening) =>
                      (filters.eligibility.gender === 'male' &&
                        opening.openToMen !== 'No' &&
                        (!filters.hasPrivateBedrooms ||
                          opening.bedroomSituation === 'Private Bedroom')) ||
                      (filters.eligibility.gender === 'female' &&
                        opening.openToWomen !== 'No' &&
                        (!filters.hasPrivateBedrooms ||
                          opening.bedroomSituation === 'Private Bedroom'))
                  )
                : (property.currentlyHasMen === 'No' &&
                    property.currentlyHasWomen === 'No') ||
                  (filters.eligibility.gender === 'male' &&
                    property.currentlyHasMen !== 'No') ||
                  (filters.eligibility.gender === 'female' &&
                    property.currentlyHasWomen !== 'No'))) &&
            (!filters.genderOfCurrentResidents ||
              (filters.genderOfCurrentResidents === 'Coed' &&
                property.currentlyHasMen !== 'No' &&
                property.currentlyHasWomen !== 'No') ||
              (filters.genderOfCurrentResidents === 'Men only' &&
                property.currentlyHasWomen === 'No') ||
              (filters.genderOfCurrentResidents === 'Women only' &&
                property.currentlyHasMen === 'No')) /* &&
            (!filters.hasPrivateBedrooms ||
              (property.openings &&
                property.openings.length &&
                property.openings.some((opening) =>
                  /private/i.test(opening.bedroomSituation || '')
                ))) */ &&
            (!filters.hasPrivateBedrooms ||
              (property.openings &&
                property.openings.length &&
                property.openings.some(
                  (opening) => opening.bedroomSituation === 'Private Bedroom'
                ))) &&
            (!filters.wheelchairSupportRequired ||
              /wheelchair/i.test(property.propertyDesign || '') ||
              (property.openings &&
                property.openings.length &&
                property.openings.some(
                  (opening) => opening.canSupportWheelchair === 'Yes'
                ))) &&
            (!filters.behavioralIssues ||
              (property.openings &&
                property.openings.length &&
                property.openings.some(
                  (opening) => opening.behavioralIssuesConsidered === 'Yes'
                )))
        ),
      (properties) =>
        properties.map((property) => {
          return {
            ...property,
            distance:
              state.selectedSort.field === 'distance' &&
              state.selectedSort.value &&
              parseFloat(
                getDistanceBetween(property, {
                  address: state.selectedSort.value,
                }).toFixed(1)
              ),
          }
        }),
      (properties) => orderBy(properties, 'name', 'asc')
    )(rootState.properties.all)
  },
  resultsSorted(state, getters) {
    return orderBy(
      getters.results,
      state.selectedSort.field,
      state.selectedSort.direction
    )
  },
}

export const mutations = {
  SET_JUST_UPDATED(state, isTrue) {
    state.justUpdated = isTrue
  },
  RESET_STATE(state) {
    assign(state, createInitialState())
  },
  MERGE_FILTERS(state, newFilters) {
    merge(state.filters, newFilters)
  },
  SET_SELECTED_SORT(state, field) {
    state.selectedSort = state.sorts.find(
      (sort) =>
        sort.field === field && (sort !== state.selectedSort || sort.default)
    )
  },
  SET_SELECTED_SORT_VALUE(state, newValue) {
    for (const sort of state.sorts) {
      if (sort.field === state.selectedSort.field) {
        sort.value = newValue
      }
    }
  },
}

const expireJustUpdated = debounce((commit) => {
  commit('SET_JUST_UPDATED', false)
}, 1000)

export const actions = {
  resetState({ commit, dispatch }) {
    const router = require('@router').default
    commit('RESET_STATE')
    commit('properties/SET_LOADED', false, { root: true })
    dispatch('properties/fetchAll', null, { root: true })
    router.push({ name: 'property-map' })

    getAnalytics().event({
      eventCategory: 'Properties Filter',
      eventAction: 'Reset',
    })
    getAnalytics().event({
      eventCategory: 'Properties Sort',
      eventAction: 'Reset',
    })
  },
  updateFilters({ commit, dispatch }, newFilters) {
    commit('MERGE_FILTERS', newFilters)
    dispatch('triggerJustUpdated')

    const newFiltersFlattened = {
      ...omit(newFilters, 'eligibility'),
      ...newFilters.eligibility,
    }
    getAnalytics().event({
      eventCategory: 'Properties Filter',
      eventAction: `Update: ${Object.keys(newFiltersFlattened).join(', ')}`,
      eventLabel: JSON.stringify(Object.values(newFiltersFlattened)),
    })
  },
  updateSelectedSort({ state, commit, dispatch }, field) {
    commit('SET_SELECTED_SORT', field)
    dispatch('triggerJustUpdated')

    getAnalytics().event({
      eventCategory: 'Properties Sort',
      eventAction: `Update: ${field}`,
      eventLabel: JSON.stringify(state.selectedSort),
    })
  },
  updateSelectedSortValue({ state, commit, dispatch }, newValue) {
    if (newValue === state.selectedSort.value) return

    commit('SET_SELECTED_SORT_VALUE', newValue)
    dispatch('triggerJustUpdated')
    if (state.selectedSort.field === 'distance' && state.selectedSort.value) {
      dispatch(
        'map/centerOnAddress',
        { address: state.selectedSort.value },
        { root: true }
      )
    }

    getAnalytics().event({
      eventCategory: 'Properties Sort',
      eventAction: `Update value for: ${state.selectedSort.field}`,
      eventLabel: JSON.stringify(state.selectedSort.value),
    })
  },
  triggerJustUpdated({ commit }) {
    commit('SET_JUST_UPDATED', true)
    expireJustUpdated(commit)
  },
}

function getAnalytics() {
  return require('vue-analytics')
}
